import React, { useState, useEffect, useMemo } from 'react';
import { Typography, Box, Paper, IconButton, Tooltip, Chip, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import VisibilityIcon from '@mui/icons-material/Visibility';
import WarningIcon from '@mui/icons-material/Warning';
import QuoteDetailsModal from './quoteDetailModal';  // Import the modal component
import ViewFilesModal from '../../shared/ViewFilesModal';

const QuotesSection = ({ bondDetails, refreshHandler }) => {
  const quotes = useMemo(() => bondDetails?.quotes || [], [bondDetails?.quotes]);
  const executingAgentID = bondDetails?.executingAgentID;
  const [countdowns, setCountdowns] = useState({});
  const [modalOpen, setModalOpen] = useState(false);  // To control modal visibility
  const [selectedQuote, setSelectedQuote] = useState(null);  // To store the selected quote data
  const [isViewFilesModalOpen, setIsViewFilesModalOpen] = useState(false);
  const [canViewPostingAgency, setCanViewPostingAgency] = useState(false);
  const [sortBy, setSortBy] = useState('none'); // Sorting state
  console.log(quotes);
  // Handle View Files Modal
  const handleOpenViewFilesModal = () => {
    setIsViewFilesModalOpen(true);
  };

  const handleCloseViewFilesModal = () => {
    setIsViewFilesModalOpen(false);
  };
  const handleViewQuoteDetails = (quote) => {
    setSelectedQuote(quote);
    setModalOpen(true);
  };
  
  // Close modal
  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedQuote(null);
  };

  // Get Fee breakdown - kept for potential tooltip use
  // eslint-disable-next-line no-unused-vars
  const getFeeBreakdown = (row) => {
    const executionFee = row?.executionFee || 0;
    const travelFee = row?.travelFee || 0;
    const otherFee = row?.otherFee || 0;

    return `Execution Fee: ${executionFee}\nTravel Fee: ${travelFee}\nOther Fee: ${otherFee}\nTotal: ${executionFee + travelFee + otherFee}`;
  };

  // Helper function to calculate total fees
  const calculateTotalFees = (row) => {
    const executionFee = row?.executionFee || 0;
    const travelFee = row?.travelFee || 0;
    const otherFee = row?.otherFee || 0;
    return executionFee + travelFee + otherFee;
  };

  const convertMinutesToHours = (minutes) => {
    return (minutes / 60).toFixed(2);
  };

  const getAcceptedQuoteMessage = () => {
    const acceptedQuoteId = bondDetails?.acceptedQuoteID; 
    const acceptedQuote = quotes.find((quote) => quote.transferBondQuoteID === acceptedQuoteId);
    if (!acceptedQuote) return 'No accepted quote found.';
  
    const postedByDate = addPostingTimeToAcceptDate(acceptedQuote.acceptedDate, acceptedQuote.promisedPostingTimeInMinutes);
  
    return (
      <>
      You've accepted a quote! This transfer bond
         was promised to be posted by <b>{postedByDate}</b>. 
        Please check back later for updates. If the bond has not been posted by the promised posting time, please contact Bankers so we can contact the posting agent.
      </>
    );
  };

  const addPostingTimeToAcceptDate = (acceptDate, postingTimeInMins) => {
    const acceptDateObj = new Date(acceptDate);
    const postingTimeInHours = postingTimeInMins / 60 ;
    acceptDateObj.setHours(acceptDateObj.getHours() + postingTimeInHours);

    const formattedDate = acceptDateObj.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }) + ' ' + acceptDateObj.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
    return formattedDate;
  }

  const calculateCountdown = (expireDate) => {
    const expirationTime = new Date(expireDate).getTime();
    const now = new Date().getTime();
    const timeDiff = expirationTime - now;

    if (timeDiff <= 0) return 'Expired';

    const hours = Math.floor(timeDiff / (1000 * 60 * 60));
    const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((timeDiff % (1000 * 60)) / 1000);

    return `${hours}h ${minutes}m ${seconds}s`;
  };

  // Sort quotes based on selected sorting option
  const sortedQuotes = useMemo(() => {
    if (sortBy === 'none') return quotes;

    const sorted = [...quotes];

    if (sortBy === 'price-asc') {
      sorted.sort((a, b) => calculateTotalFees(a) - calculateTotalFees(b));
    } else if (sortBy === 'price-desc') {
      sorted.sort((a, b) => calculateTotalFees(b) - calculateTotalFees(a));
    } else if (sortBy === 'time-asc') {
      sorted.sort((a, b) => a.promisedPostingTimeInMinutes - b.promisedPostingTimeInMinutes);
    } else if (sortBy === 'time-desc') {
      sorted.sort((a, b) => b.promisedPostingTimeInMinutes - a.promisedPostingTimeInMinutes);
    }

    return sorted;
  }, [quotes, sortBy]);

  // Update countdowns every second
  useEffect(() => {
    const interval = setInterval(() => {
      const newCountdowns = {};
      quotes.forEach((quote) => {
        newCountdowns[quote.transferBondQuoteID] = calculateCountdown(quote.expireDate);
      });
      setCountdowns(newCountdowns);
    }, 1000);

    return () => clearInterval(interval);
  }, [quotes]);


  // Fetch User Permissions
  useEffect(() => {
    const fetchPermissions = async () => {
      const token = localStorage.getItem('jwtToken');
      const baseURL = process.env.REACT_APP_BASE_URL;

      if (!token) return;

      try {
        const userDetailsResponse = await fetch(`${baseURL}user/info`, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (!userDetailsResponse.ok) {
          throw new Error('Error fetching user details');
        }

        const userDetails = await userDetailsResponse.json();
        const { subAgentId, agencyId } = userDetails;

        const permissionsResponse = await fetch(
          `${baseURL}User/${agencyId}/permissions/${subAgentId}`,
          {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        );

        if (!permissionsResponse.ok) {
          throw new Error('Error fetching permissions');
        }

        const permissions = await permissionsResponse.json();
        const hasPermission = permissions.some(
          (perm) => perm.portalPermissionTypeID === 9 && perm.permit === true
        );

        setCanViewPostingAgency(hasPermission);
      } catch (error) {
        console.error('Error fetching permissions:', error);
      }
    };

    fetchPermissions();
  }, []);

  const quoteColumns = [
    // {
    //   field: 'totalFees',
    //   headerName: 'Total Fees',
    //   width: 100,
    //   valueGetter: (value, row) => `$${calculateTotalFees(row).toFixed(2)}`,
    // },
    {
      field: 'executionFee',
      headerName: 'Execution Fee',
      width: 125,
      valueGetter: (value) => `${value ? `$${value.toFixed(2)}` : '-'}`,
    },
    {
      field: 'travelFee',
      headerName: 'Travel Fee',
      width: 100,
      valueGetter: (value) => `${value ? `$${value.toFixed(2)}` : '-'}`,
    },
    {
      field: 'otherFee',
      headerName: 'Misc Fee',
      width: 100,
      valueGetter: (value) => `${value ? `$${value.toFixed(2)}` : '-'}`,
    },
    {
      field: 'promisedPostingTimeInMinutes',
      headerName: 'Post Time (hrs)',
      width: 125,
      valueGetter: (value) => convertMinutesToHours(value),
    },
    {
      field: 'expireDate',
      headerName: 'Expires',
      width: 125,
      valueGetter: (value) => countdowns[value] || calculateCountdown(value),
    },
    {
      field: 'feeDescription',
      headerName: 'Fee Description',
      width: 250,
      valueGetter: (value) => value || '-',
    },
    {
      field: 'postingStipulations',
      headerName: 'Posting Stipulations',
      width: 250,
      renderCell: (params) => {
        const stipulations = params.value || '-';
        return (
          <Tooltip
            title={stipulations}
            placement="top"
            arrow
            componentsProps={{
              tooltip: {
                sx: {
                  fontSize: '1rem',
                  maxWidth: '500px'
                }
              }
            }}
          >
            <span style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              display: 'block',
              cursor: stipulations !== '-' ? 'help' : 'default'
            }}>
              {stipulations}
            </span>
          </Tooltip>
        );
      },
    },
    {
      field: 'paymentRequiredUpFront',
      headerName: 'Payment Required',
      width: 150,
      renderCell: (params) => {
        if (params.value) {
          return (
            <Tooltip title="This quote requires payment up front. If selected, bond will be taken offline." arrow>
              <Chip
                icon={<WarningIcon />}
                label="Upfront Payment"
                color="warning"
                size="small"
              />
            </Tooltip>
          );
        }
        return '-';
      },
    },
    ...(canViewPostingAgency
      ? [
          {
            field: 'dbaAgencyName',
            headerName: 'Posting Agent',
            width: 250,
            renderCell: (params) => (
              <span>{params.row.dbaAgencyName || '-'}</span>
            ),
          },
        ]
      : []),
    {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      renderCell: (params) => (
        <IconButton
          color="primary"
          onClick={() => handleViewQuoteDetails(params.row)}  // Correctly pass the clicked row data
        >
          <VisibilityIcon />
        </IconButton>
      ),
    },
  ];

  if (bondDetails?.statusCd === "C") {
    return (
      <Box sx={{ mt: 4 }}>
        <Paper
          sx={{
            padding: 2,
            backgroundColor: '#f9f9f9',
            borderRadius: 2,
            boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
            border: '1px solid #e0e0e0',
          }}
        >
          <Typography variant="h6">
            Closed
          </Typography>
          <Typography variant="body-1">
            This transaction is complete. No further action is required. 
          </Typography>
          <br />
          <Tooltip title="View Uploaded Files">
            <IconButton color="primary" onClick={handleOpenViewFilesModal}>
              <VisibilityIcon />
            </IconButton>
            <span style={{ marginLeft: 5 }}>View uploaded Files</span>
          </Tooltip>
        </Paper>

        <ViewFilesModal
        open={isViewFilesModalOpen}
        onClose={handleCloseViewFilesModal}
        bondDetails={bondDetails}
      />
      </Box>
    );
  }

  if (bondDetails?.hasBeenPosted) {
    return (
      <Box sx={{ mt: 4 }}>
        <Paper
          sx={{
            padding: 2,
            backgroundColor: '#f9f9f9',
            borderRadius: 2,
            boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
            border: '1px solid #e0e0e0',
          }}
        >
          <Typography variant="h6">
            Bankers Verification
          </Typography>
          <Typography variant="body-1">
            The Posting Agent has uploaded the powers and indicated the bond(s) have been posted.
            Once a Bankers team member has verified the posting. This transaction will be closed.  
          </Typography>
          <br />
          <Tooltip title="View Uploaded Files">
            <IconButton color="primary" onClick={handleOpenViewFilesModal}>
              <VisibilityIcon />
            </IconButton>
            <span style={{ marginLeft: 5 }}>View uploaded Files</span>
          </Tooltip>
        </Paper>
        <ViewFilesModal
        open={isViewFilesModalOpen}
        onClose={handleCloseViewFilesModal}
        bondDetails={bondDetails}
      />
      </Box>
    );
  }

  if (bondDetails?.isDeferred === "Y") {
    return (
      <Box sx={{ mt: 4 }}>
        <Paper
          sx={{
            padding: 2,
            backgroundColor: '#f9f9f9',
            borderRadius: 2,
            boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
            border: '1px solid #e0e0e0',
          }}
        >
          <Typography variant="h6" sx={{ color: 'red' }}>
            This Request has been cancelled. The information below is for reference only.
          </Typography>
        </Paper>
      </Box>
    );
  }
  // Conditions for rendering the quotes section
  if (quotes.length === 0 && !executingAgentID && (bondDetails?.isDeferred === "N")) {
    return (
      <Box sx={{ mt: 4 }}>
        <Paper
          sx={{
            padding: 2,
            backgroundColor: '#f9f9f9',
            borderRadius: 2,
            boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
            border: '1px solid #e0e0e0',
          }}
        >
          <Typography variant="h6">Waiting for quotes...</Typography>
          <Typography>Quotes will automatically appear here when they become available.</Typography>

        </Paper>
      </Box>
    );
  }

  if (executingAgentID && (bondDetails?.isDeferred === "N")) {
    return (
      <Box sx={{ mt: 4 }}>
        <Paper
          sx={{
            padding: 2,
            backgroundColor: '#f9f9f9',
            borderRadius: 2,
            boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
            border: '1px solid #e0e0e0',
          }}
        >
          <Typography variant="h6">An Agent is Posting!</Typography>
          <Typography>{getAcceptedQuoteMessage()}</Typography>
        </Paper>
      </Box>
    );
  }

  if (quotes.length > 0 && !executingAgentID && (bondDetails?.isDeferred === "N")) {
    return (
      <Box sx={{ mt: 4 }}>
        <Paper
          sx={{
            padding: 2,
            backgroundColor: '#f9f9f9',
            borderRadius: 2,
            boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
            border: '1px solid #e0e0e0',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6">
              Quotes
            </Typography>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel id="sort-quotes-label">Sort by</InputLabel>
              <Select
                labelId="sort-quotes-label"
                id="sort-quotes-select"
                value={sortBy}
                label="Sort by"
                onChange={(e) => setSortBy(e.target.value)}
              >
                <MenuItem value="none">None (Default)</MenuItem>
                <MenuItem value="price-asc">Price: Low to High</MenuItem>
                <MenuItem value="price-desc">Price: High to Low</MenuItem>
                <MenuItem value="time-asc">Posting Time: Fastest First</MenuItem>
                <MenuItem value="time-desc">Posting Time: Slowest First</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <DataGrid
            rows={sortedQuotes}
            columns={quoteColumns}
            pageSize={5}
            rowsPerPageOptions={[5]}
            autoHeight
            disableSelectionOnClick
            getRowId={(row) => row.transferBondQuoteID}
          />
        </Paper>
        <QuoteDetailsModal
          open={modalOpen}
          handleClose={handleCloseModal}
          quote={selectedQuote}
          bondDetails={bondDetails}
          refreshHandler={refreshHandler}
          />
      </Box>
    );
  }
};

export default QuotesSection;
